import i18next from 'i18next';
import { QuerySuggestionResponse } from '../@types/QuerySuggestion';
import { extractDataFromElement } from '../utils/data';
import ready from '../utils/ready';
import sanitize from '../utils/sanitize';

interface SearchBarProps {
  pathname: string;
  url: string;
  placeholder?: string;
}

let props: SearchBarProps | undefined;
let timeout: number;
let timeoutFocusOut: number;
let queryIndex: number = 1;

const initializeSearchBar = () => {
  const searchBar = document.querySelector('.searchBar');
  const button = searchBar?.querySelector('button');
  const inputField = searchBar?.querySelector('input');
  const suggestionContainer = searchBar?.querySelector('.search');
  props = extractDataFromElement<SearchBarProps>(searchBar, 'data-props');
  if (!searchBar || !button || !inputField || !suggestionContainer || !props) return;

  const getSuggestions = async (value: string) => {
    queryIndex++;
    if (!value) {
      renderSuggestionList();
      return;
    }
    const url = `${props?.url}?q=${value}`;
    let suggestionData: QuerySuggestionResponse | undefined;
    try {
      const localQueryIndex = queryIndex;
      const response = await fetch(url);
      if (localQueryIndex !== queryIndex) {
        return;
      }
      if (response.ok) {
        suggestionData = (await response.json()) as QuerySuggestionResponse;
      } else {
        suggestionData = undefined;
      }
      renderSuggestionList(suggestionData);
    } catch (_e) {
      renderSuggestionList();
    }
  };

  const createSearchFoldout = () => {
    const node = document.createElement('div');
    node.className = 'search__foldout';
    node.innerHTML = sanitize(`
      <div class="container">
        <div class="search__foldout-label">${i18next.t('other.searchResults')}</div>
        <ul class="search__foldout-list"></ul>
        ${
          window.location.pathname.indexOf(props?.pathname || '') === -1
            ? `
            <div class="search__foldout-action">
              <button class="button button--primary" title="${i18next.t('other.showAllCars')}">
                <span class="button__content">${i18next.t('other.showAllCars')}</span>
              </button>
            </div>
            `
            : ''
        }
      </div>
    `);

    node.querySelector('button')?.addEventListener('click', () => {
      if (props?.pathname) {
        window.location.assign(props.pathname);
      }
    });

    return node;
  };

  const suggestionClick = (label: string) => {
    performSearch(label);
  };

  const onKeyDown = (e: KeyboardEvent) => {
    const key = e.key;
    const activeClass = 'search__foldout-btn--is-active';
    const nodes = suggestionContainer.querySelectorAll<HTMLButtonElement>(
      'button.search__foldout-btn'
    );
    const activeIndex = Array.from(nodes).findIndex((n) => n.classList.contains(activeClass));

    if (key === 'Enter') {
      if (!!nodes.length && activeIndex > -1 && Array.from(nodes)?.[activeIndex]) {
        Array.from(nodes)[activeIndex].click();
      } else {
        performSearch(inputField.value);
      }
    } else if (key === 'ArrowDown' || key === 'ArrowUp') {
      const size = nodes.length - 1;
      const nextIndex = activeIndex + (key === 'ArrowDown' ? 1 : -1);

      let nextActive = nextIndex;
      if (nextIndex < 0) {
        nextActive = size;
      } else if (nextIndex > size) {
        nextActive = 0;
      }

      nodes.forEach((node, index) => {
        if (index === nextActive) {
          node.classList.add(activeClass);
        } else {
          node.classList.remove(activeClass);
        }
      });
    } else {
      nodes.forEach((node) => {
        node.classList.remove(activeClass);
      });
    }
  };

  const renderSuggestionList = (data?: QuerySuggestionResponse) => {
    const existingNode = suggestionContainer.querySelector('.search__foldout');
    const node = existingNode ? existingNode : createSearchFoldout();
    if (!data?.suggestions.length) {
      node.remove();
      return;
    }

    const ulNode = node.querySelector('ul');
    if (ulNode && data) {
      ulNode.innerHTML = sanitize(
        data.suggestions
          .map(
            (suggestion) => `
            <li class="search__foldout-item">
              <button class="search__foldout-btn">
                <span class="search__foldout-btn-label theme theme__font--bold">${suggestion.label}</span>
                <span class="search__foldout-btn-info">${suggestion.term_count}</span>
              </button>
            </li>
          `
          )
          .join('')
      );
      ulNode.querySelectorAll('button').forEach((button, index) => {
        button.addEventListener('click', () => {
          clearTimeout(timeoutFocusOut);
          suggestionClick(data.suggestions[index].label);
        });
      });
    }
    if (!existingNode) {
      suggestionContainer.appendChild(node);
    }
  };

  const debounce = (value: string) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      getSuggestions(value);
    }, 250) as unknown as number;
  };

  const performSearch = (value: string) => {
    clearTimeout(timeout);
    queryIndex++;
    const pathname = props?.pathname;

    const searchString = value ? `?query=${value}` : '';

    if (window.location.pathname === pathname && window.WaykePubSub2.publish) {
      if (window.WaykePubSub2.publish) {
        window.WaykePubSub2.publish('onQueryUpdate', searchString);
      }
      inputField.value = '';
      renderSuggestionList();
    } else {
      window.location.assign(`${pathname}${searchString}`);
    }
  };

  button.addEventListener('click', () => {
    clearTimeout(timeoutFocusOut);
    performSearch(inputField.value);
  });
  inputField.addEventListener('focusout', () => {
    clearTimeout(timeout);
    queryIndex++;
    timeoutFocusOut = setTimeout(() => {
      renderSuggestionList();
    }, 200) as unknown as number;
  });
  inputField.addEventListener('focusin', (e) => {
    const value = (e.currentTarget as HTMLInputElement).value;
    if (value) {
      getSuggestions(value);
    }
  });
  inputField.addEventListener('keydown', (e) => onKeyDown(e));
  inputField.addEventListener('input', (e) =>
    debounce((e.currentTarget as HTMLInputElement).value)
  );
};

ready(initializeSearchBar);
